import FYIJobSelect from '../../../../components/fyi-job-select/FYIJobSelect';
import DocumentResync from '../../DocumentResync';
import IntegrationOverview from '../../../../components/integration-overview/IntegrationOverview';
import FYILogo from '../../../../assets/fyi.png';
import FYIJobChangeConfirmation from '../../FYIJobChangeConfirmation';
import ErrorSplashScreen from '../../../../pages/ErrorSplashScreen';
import { useState } from 'react';
import { toastNotification } from '../../../../components/toast/Toast';
import { useUpdateWorkpapers } from '../../../../queries/use-update-workpapers';
import { Link } from 'react-router-dom';
import { Button } from '@cimplico/components';
import { useFirmContext } from '../../../../providers/FirmProvider';
import { useWorkpaperContext } from '../../../../providers/WorkpaperProvider';

function JobIntegration(): JSX.Element {
    const { integrations, id, entity } = useWorkpaperContext();

    const fyi = integrations?.fyi?.job;
    const updateWorkpapersQuery = useUpdateWorkpapers();
    const { shortId } = useFirmContext();
    const [fyiAction, setFYIAction] = useState<'replacing' | 'removing'>();
    const [job, setJob] = useState<{ id: string; name: string } | undefined>(fyi ? { name: fyi.name, id: fyi.id } : undefined);

    function confirm(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();
        if (job && fyi && fyi?.id !== job.id) {
            setFYIAction('replacing');
            return;
        }
        // Remove job
        else if (!job && fyi) {
            setFYIAction('removing');
            return;
        }

        handleSubmit();
    }

    function handleSubmit(): void {
        let req;
        // Job has changed
        if (job && fyi?.id !== job.id) {
            req = { fyiJob: job.id };
        }
        // Remove job
        else if (!job && fyi) {
            req = { fyiJob: null };
        }
        // New job
        else if (job && !fyi) {
            req = { fyiJob: job.id };
        }

        if (req) {
            updateWorkpapersQuery.mutate([{ id, fields: { integrations: req } }], {
                onSuccess: () => {
                    toastNotification('success', 'Successfully updated job mapping!');
                },
                onError: () => {
                    toastNotification('error', 'Unable to update job mapping!');
                },
            });
        }
    }

    if (!entity) {
        return (
            <ErrorSplashScreen
                errorCode={404}
                title="Entity not found."
                redirectTitle="Back to entities"
                redirectUrl={`/${shortId}/entities`}
                leftArrow={true}
            />
        );
    }

    return (
        <>
            {fyiAction && (
                <FYIJobChangeConfirmation
                    mode={fyiAction}
                    onClose={(): void => setFYIAction(undefined)}
                    onConfirm={(): void => {
                        setFYIAction(undefined);
                        handleSubmit();
                    }}
                />
            )}
            <IntegrationOverview
                title="FYI"
                description="Pull your documents, clients and groups from FYI."
                active={Boolean(fyi)}
                logo={FYILogo}
                supportLink="https://learn.cimplico.com/docs/fyi-files#manage-the-connected-job"
            >
                <div className="max-w-[800px]">
                    {entity.integrations?.fyi ? (
                        <div className="mt-4">
                            <div className="w-1/2 pr-4">
                                <form onSubmit={confirm}>
                                    <FYIJobSelect
                                        isOptional
                                        onChange={setJob}
                                        selectedJob={job ? { name: job.name, id: job.id } : undefined}
                                        title="Job"
                                        placeholder="Select a job..."
                                        fyiEntityId={entity.integrations!.fyi!.id}
                                    />
                                    <div className="mt-2 w-fit ml-auto">
                                        <Button
                                            type="primary"
                                            size="sm"
                                            label={updateWorkpapersQuery.isLoading ? 'Saving...' : 'Save'}
                                            isSubmit
                                            disabled={updateWorkpapersQuery.isLoading}
                                            isLoading={updateWorkpapersQuery.isLoading}
                                        />
                                    </div>
                                </form>

                                {integrations?.fyi?.job && (
                                    <div className="mt-4">
                                        <DocumentResync workpaperId={id} />
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <p className="text-gray-600 text-sm mt-2">
                            Please configure <strong>{entity.name}</strong> with FYI on the{' '}
                            <Link
                                className="text-indigo-600"
                                to={`/${shortId}/entities/${entity.id}?tab=integrations&focusedIntegration=fyi`}
                            >
                                entity integrations page
                            </Link>
                            .
                        </p>
                    )}
                </div>
            </IntegrationOverview>
        </>
    );
}

export default JobIntegration;
